import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import './AdminRoles.css';

function AdminRoles() {
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [categories, setCategories] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Selected role for editing
    const [selectedRole, setSelectedRole] = useState(null);
    const [rolePermissions, setRolePermissions] = useState({});
    const [savingPermissions, setSavingPermissions] = useState(false);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);
    const [showPermissionMatrix, setShowPermissionMatrix] = useState(false);
    const [allRolePermissions, setAllRolePermissions] = useState({});

    // Form data
    const [formData, setFormData] = useState({
        name: '',
        display_name: '',
        description: ''
    });

    // Category labels in Thai
    const categoryLabels = {
        dashboard: 'แดชบอร์ด',
        salary: 'สลิปเงินเดือน',
        tax: 'หนังสือ 50 ทวิ',
        request: 'คำร้อง',
        chat: 'แชท',
        report: 'รายงาน',
        users: 'จัดการผู้ใช้',
        roles: 'จัดการ Role',
        settings: 'ตั้งค่า',
        system: 'ระบบ'
    };

    const fetchRoles = useCallback(async () => {
        try {
            const response = await api.get('/roles');
            setRoles(response.data);
        } catch (err) {
            setError('ไม่สามารถโหลดรายการ Role ได้');
        }
    }, []);

    const fetchPermissions = useCallback(async () => {
        try {
            const response = await api.get('/roles/permissions/all');
            setPermissions(response.data.permissions || []);
            setCategories(response.data.grouped || {});
        } catch (err) {
            console.error('Fetch permissions error:', err);
        }
    }, []);

    const fetchRolePermissions = useCallback(async (roleId) => {
        try {
            const response = await api.get(`/roles/${roleId}/permissions`);
            const permMap = {};
            (response.data.permissions || []).forEach(p => {
                permMap[p.id] = p.granted === 1 || p.granted === true;
            });
            setRolePermissions(permMap);
        } catch (err) {
            setError('ไม่สามารถโหลดสิทธิ์ของ Role ได้');
        }
    }, []);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchRoles(), fetchPermissions()]);
            setLoading(false);
        };
        loadData();
    }, [fetchRoles, fetchPermissions]);

    useEffect(() => {
        if (selectedRole) {
            fetchRolePermissions(selectedRole.id);
        } else {
            setRolePermissions({});
        }
    }, [selectedRole, fetchRolePermissions]);

    const handleSelectRole = (role) => {
        setSelectedRole(role);
        setError('');
        setSuccess('');
    };

    const handleTogglePermission = async (permId) => {
        if (!selectedRole) return;

        const newValue = !rolePermissions[permId];
        setRolePermissions(prev => ({ ...prev, [permId]: newValue }));

        try {
            await api.post(`/roles/${selectedRole.id}/permissions/${permId}`, {
                granted: newValue
            });
        } catch (err) {
            // Revert on error
            setRolePermissions(prev => ({ ...prev, [permId]: !newValue }));
            setError('ไม่สามารถเปลี่ยนสิทธิ์ได้');
        }
    };

    const handleToggleAllInCategory = async (category, enable) => {
        if (!selectedRole) return;

        const categoryPerms = categories[category] || [];
        const updates = {};
        categoryPerms.forEach(p => {
            updates[p.id] = enable;
        });

        setRolePermissions(prev => ({ ...prev, ...updates }));

        setSavingPermissions(true);
        try {
            // Update all permissions in category
            for (const perm of categoryPerms) {
                await api.post(`/roles/${selectedRole.id}/permissions/${perm.id}`, {
                    granted: enable
                });
            }
            setSuccess(`${enable ? 'เปิด' : 'ปิด'}สิทธิ์ทั้งหมดใน ${categoryLabels[category]} สำเร็จ`);
        } catch (err) {
            setError('เกิดข้อผิดพลาดในการเปลี่ยนสิทธิ์');
            fetchRolePermissions(selectedRole.id); // Reload on error
        } finally {
            setSavingPermissions(false);
        }
    };

    const handleCreateRole = async (e) => {
        e.preventDefault();
        setError('');

        try {
            await api.post('/roles', formData);
            setSuccess('สร้าง Role สำเร็จ');
            setShowCreateModal(false);
            setFormData({ name: '', display_name: '', description: '' });
            fetchRoles();
        } catch (err) {
            setError(err.response?.data?.error || 'ไม่สามารถสร้าง Role ได้');
        }
    };

    const handleEditRole = async (e) => {
        e.preventDefault();
        if (!selectedRole) return;
        setError('');

        try {
            await api.put(`/roles/${selectedRole.id}`, {
                display_name: formData.display_name,
                description: formData.description,
                is_active: selectedRole.is_active
            });
            setSuccess('อัพเดท Role สำเร็จ');
            setShowEditModal(false);
            fetchRoles();
            // Update selected role
            setSelectedRole(prev => ({
                ...prev,
                display_name: formData.display_name,
                description: formData.description
            }));
        } catch (err) {
            setError(err.response?.data?.error || 'ไม่สามารถอัพเดท Role ได้');
        }
    };

    const handleDeleteRole = async () => {
        if (!roleToDelete) return;

        try {
            await api.delete(`/roles/${roleToDelete.id}`);
            setSuccess('ลบ Role สำเร็จ');
            setShowDeleteModal(false);
            setRoleToDelete(null);
            if (selectedRole?.id === roleToDelete.id) {
                setSelectedRole(null);
            }
            fetchRoles();
        } catch (err) {
            setError(err.response?.data?.error || 'ไม่สามารถลบ Role ได้');
        }
    };

    const openCreateModal = () => {
        setFormData({ name: '', display_name: '', description: '' });
        setShowCreateModal(true);
        setError('');
    };

    const openEditModal = () => {
        if (!selectedRole) return;
        setFormData({
            name: selectedRole.name,
            display_name: selectedRole.display_name,
            description: selectedRole.description || ''
        });
        setShowEditModal(true);
        setError('');
    };

    const openDeleteModal = (role) => {
        setRoleToDelete(role);
        setShowDeleteModal(true);
        setError('');
    };

    const getCategoryPermissionCount = (category) => {
        const categoryPerms = categories[category] || [];
        const granted = categoryPerms.filter(p => rolePermissions[p.id]).length;
        return { granted, total: categoryPerms.length };
    };

    const fetchAllRolePermissions = async () => {
        try {
            const allPerms = {};
            for (const role of roles) {
                const response = await api.get(`/roles/${role.id}/permissions`);
                const permMap = {};
                (response.data.permissions || []).forEach(p => {
                    permMap[p.id] = p.granted === 1 || p.granted === true;
                });
                allPerms[role.id] = permMap;
            }
            setAllRolePermissions(allPerms);
        } catch (err) {
            console.error('Failed to fetch all role permissions:', err);
        }
    };

    const hasPermission = (roleId, permId) => {
        return allRolePermissions[roleId]?.[permId] || false;
    };

    if (loading) {
        return (
            <div className="admin-roles-page">
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>กำลังโหลดข้อมูล...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-roles-page">
            <div className="page-header">
                <div className="header-content">
                    <h1>จัดการ Roles & Permissions</h1>
                    <p>กำหนดสิทธิ์การเข้าถึงระบบตาม Role</p>
                </div>
                <div className="header-actions">
                    <button
                        className="steam-btn steam-btn-secondary"
                        onClick={() => {
                            setShowPermissionMatrix(true);
                            if (Object.keys(allRolePermissions).length === 0) {
                                fetchAllRolePermissions();
                            }
                        }}
                    >
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="7" height="7"></rect>
                            <rect x="14" y="3" width="7" height="7"></rect>
                            <rect x="14" y="14" width="7" height="7"></rect>
                            <rect x="3" y="14" width="7" height="7"></rect>
                        </svg>
                        ตารางสิทธิ์
                    </button>
                    <button className="steam-btn steam-btn-primary" onClick={openCreateModal}>
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 5v14M5 12h14" />
                        </svg>
                        สร้าง Role ใหม่
                    </button>
                </div>
            </div>

            {error && (
                <div className="alert alert-error">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    {error}
                    <button onClick={() => setError('')}>&times;</button>
                </div>
            )}

            {success && (
                <div className="alert alert-success">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    {success}
                    <button onClick={() => setSuccess('')}>&times;</button>
                </div>
            )}

            <div className="roles-layout">
                {/* Left Panel - Role List */}
                <div className="roles-list-panel">
                    <div className="panel-header">
                        <h2>รายการ Roles</h2>
                        <span className="count-badge">{roles.length}</span>
                    </div>
                    <div className="roles-list">
                        {roles.map(role => (
                            <div
                                key={role.id}
                                className={`role-item ${selectedRole?.id === role.id ? 'selected' : ''} ${!role.is_active ? 'inactive' : ''}`}
                                onClick={() => handleSelectRole(role)}
                            >
                                <div className="role-info">
                                    <div className="role-name">
                                        {role.display_name}
                                        {role.is_system && (
                                            <span className="system-badge" title="System Role">
                                                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                                                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                                                </svg>
                                            </span>
                                        )}
                                    </div>
                                    <div className="role-meta">
                                        <span className="role-code">{role.name}</span>
                                        <span className="role-stats">
                                            <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                            </svg>
                                            {role.user_count} คน
                                        </span>
                                        <span className="role-stats">
                                            <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                                                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/>
                                            </svg>
                                            {role.permission_count} สิทธิ์
                                        </span>
                                    </div>
                                </div>
                                {!role.is_system && (
                                    <button
                                        className="delete-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openDeleteModal(role);
                                        }}
                                        title="ลบ Role"
                                    >
                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                        </svg>
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right Panel - Permissions */}
                <div className="permissions-panel">
                    {selectedRole ? (
                        <>
                            <div className="panel-header">
                                <div className="selected-role-info">
                                    <h2>{selectedRole.display_name}</h2>
                                    <p>{selectedRole.description || 'ไม่มีคำอธิบาย'}</p>
                                </div>
                                <button className="steam-btn steam-btn-secondary" onClick={openEditModal}>
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                    แก้ไข Role
                                </button>
                            </div>

                            <div className="permissions-content">
                                {savingPermissions && (
                                    <div className="saving-overlay">
                                        <div className="spinner"></div>
                                        กำลังบันทึก...
                                    </div>
                                )}

                                {Object.keys(categories).map(category => {
                                    const { granted, total } = getCategoryPermissionCount(category);
                                    const allGranted = granted === total;
                                    const noneGranted = granted === 0;

                                    return (
                                        <div key={category} className="permission-category">
                                            <div className="category-header">
                                                <div className="category-title">
                                                    <h3>{categoryLabels[category] || category}</h3>
                                                    <span className="category-count">{granted}/{total}</span>
                                                </div>
                                                <div className="category-actions">
                                                    <button
                                                        className={`toggle-all-btn ${allGranted ? 'active' : ''}`}
                                                        onClick={() => handleToggleAllInCategory(category, true)}
                                                        disabled={allGranted}
                                                    >
                                                        เปิดทั้งหมด
                                                    </button>
                                                    <button
                                                        className={`toggle-all-btn ${noneGranted ? 'active' : ''}`}
                                                        onClick={() => handleToggleAllInCategory(category, false)}
                                                        disabled={noneGranted}
                                                    >
                                                        ปิดทั้งหมด
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="permission-list">
                                                {(categories[category] || []).map(perm => (
                                                    <div key={perm.id} className="permission-item">
                                                        <label className="permission-toggle">
                                                            <input
                                                                type="checkbox"
                                                                checked={rolePermissions[perm.id] || false}
                                                                onChange={() => handleTogglePermission(perm.id)}
                                                            />
                                                            <span className="toggle-slider"></span>
                                                        </label>
                                                        <div className="permission-info">
                                                            <div className="permission-name">{perm.display_name}</div>
                                                            <div className="permission-code">{perm.name}</div>
                                                            {perm.description && (
                                                                <div className="permission-desc">{perm.description}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <div className="no-selection">
                            <svg viewBox="0 0 24 24" width="64" height="64" fill="currentColor" opacity="0.3">
                                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                            </svg>
                            <h3>เลือก Role เพื่อจัดการสิทธิ์</h3>
                            <p>คลิกที่ Role ทางด้านซ้ายเพื่อดูและแก้ไขสิทธิ์</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Role Modal */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>สร้าง Role ใหม่</h2>
                            <button className="close-btn" onClick={() => setShowCreateModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleCreateRole}>
                            <div className="form-group">
                                <label>ชื่อ Role (ภาษาอังกฤษตัวเล็ก)</label>
                                <input
                                    type="text"
                                    className="steam-input"
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/[^a-z_]/g, '') })}
                                    placeholder="เช่น finance_officer"
                                    required
                                />
                                <small>ใช้เฉพาะ a-z และ underscore เท่านั้น</small>
                            </div>
                            <div className="form-group">
                                <label>ชื่อแสดง</label>
                                <input
                                    type="text"
                                    className="steam-input"
                                    value={formData.display_name}
                                    onChange={e => setFormData({ ...formData, display_name: e.target.value })}
                                    placeholder="เช่น เจ้าหน้าที่การเงิน"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>คำอธิบาย</label>
                                <textarea
                                    className="steam-input"
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="อธิบายหน้าที่ของ Role นี้"
                                    rows={3}
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="steam-btn steam-btn-secondary" onClick={() => setShowCreateModal(false)}>
                                    ยกเลิก
                                </button>
                                <button type="submit" className="steam-btn steam-btn-primary">
                                    สร้าง Role
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Role Modal */}
            {showEditModal && selectedRole && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>แก้ไข Role: {selectedRole.name}</h2>
                            <button className="close-btn" onClick={() => setShowEditModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleEditRole}>
                            <div className="form-group">
                                <label>ชื่อ Role</label>
                                <input
                                    type="text"
                                    className="steam-input"
                                    value={formData.name}
                                    disabled
                                />
                                <small>ไม่สามารถเปลี่ยนชื่อ Role ได้</small>
                            </div>
                            <div className="form-group">
                                <label>ชื่อแสดง</label>
                                <input
                                    type="text"
                                    className="steam-input"
                                    value={formData.display_name}
                                    onChange={e => setFormData({ ...formData, display_name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>คำอธิบาย</label>
                                <textarea
                                    className="steam-input"
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    rows={3}
                                />
                            </div>
                            {selectedRole.is_system && (
                                <div className="alert alert-info" style={{ marginBottom: '16px' }}>
                                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                                    </svg>
                                    นี่คือ System Role - สามารถแก้ไขได้เฉพาะชื่อแสดงและคำอธิบาย
                                </div>
                            )}
                            <div className="modal-actions">
                                <button type="button" className="steam-btn steam-btn-secondary" onClick={() => setShowEditModal(false)}>
                                    ยกเลิก
                                </button>
                                <button type="submit" className="steam-btn steam-btn-primary">
                                    บันทึก
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Permission Matrix Modal */}
            {showPermissionMatrix && (
                <div className="modal-overlay" onClick={() => setShowPermissionMatrix(false)}>
                    <div className="modal-content modal-large permission-matrix-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>ตารางเปรียบเทียบสิทธิ์ทั้งหมด</h2>
                            <button className="close-btn" onClick={() => setShowPermissionMatrix(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            {Object.keys(allRolePermissions).length === 0 ? (
                                <div className="loading-container">
                                    <div className="spinner"></div>
                                    <p>กำลังโหลดข้อมูลสิทธิ์...</p>
                                </div>
                            ) : (
                                <div className="permission-matrix-container">
                                    {Object.keys(categories).map(category => {
                                        const categoryPerms = categories[category] || [];
                                        if (categoryPerms.length === 0) return null;

                                        return (
                                            <div key={category} className="matrix-category">
                                                <h3 className="matrix-category-title">
                                                    {categoryLabels[category] || category}
                                                </h3>
                                                <div className="matrix-table-wrapper">
                                                    <table className="permission-matrix-table">
                                                        <thead>
                                                            <tr>
                                                                <th className="permission-col">สิทธิ์</th>
                                                                {roles.map(role => (
                                                                    <th key={role.id} className="role-col">
                                                                        <div className="role-header">
                                                                            <span className="role-display-name">{role.display_name}</span>
                                                                            <span className="role-name-code">{role.name}</span>
                                                                        </div>
                                                                    </th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {categoryPerms.map(perm => (
                                                                <tr key={perm.id}>
                                                                    <td className="permission-col">
                                                                        <div className="perm-cell">
                                                                            <div className="perm-display-name">{perm.display_name}</div>
                                                                            <div className="perm-name-code">{perm.name}</div>
                                                                        </div>
                                                                    </td>
                                                                    {roles.map(role => (
                                                                        <td key={role.id} className="role-col">
                                                                            <div className="matrix-cell">
                                                                                {hasPermission(role.id, perm.id) ? (
                                                                                    <svg viewBox="0 0 24 24" width="20" height="20" fill="#10b981">
                                                                                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                                                                    </svg>
                                                                                ) : (
                                                                                    <svg viewBox="0 0 24 24" width="20" height="20" fill="#ef4444">
                                                                                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                                                                    </svg>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="modal-actions">
                            <button className="steam-btn steam-btn-secondary" onClick={() => setShowPermissionMatrix(false)}>
                                ปิด
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Role Modal */}
            {showDeleteModal && roleToDelete && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content modal-small" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>ยืนยันการลบ Role</h2>
                            <button className="close-btn" onClick={() => setShowDeleteModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="delete-warning">
                                <svg viewBox="0 0 24 24" width="48" height="48" fill="#f59e0b">
                                    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                                </svg>
                                <p>คุณต้องการลบ Role <strong>{roleToDelete.display_name}</strong> ใช่หรือไม่?</p>
                                {roleToDelete.user_count > 0 && (
                                    <p className="error-text">
                                        ไม่สามารถลบได้ - มีผู้ใช้ {roleToDelete.user_count} คนที่ใช้ Role นี้อยู่
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button className="steam-btn steam-btn-secondary" onClick={() => setShowDeleteModal(false)}>
                                ยกเลิก
                            </button>
                            <button
                                className="steam-btn steam-btn-danger"
                                onClick={handleDeleteRole}
                                disabled={roleToDelete.user_count > 0}
                            >
                                ลบ Role
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminRoles;
